import { useEffect, useRef, useState, useCallback } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { screenToContainerPoint, dragOffsetToMap } from "@/lib/mapRotation";
import { angleDelta, normalizeDeg } from "@/lib/mapNavigation";

// SKYCORE MAP.NAV.1 — moteur de rotation interne (cf. lib/mapRotation.js). Tout est installé sur
// l'INSTANCE de carte (jamais sur L.Map.prototype) et retiré au démontage : aucun listener résiduel.

// Taille du cadre visible (ResizeObserver unique, déconnecté au démontage).
export function useElementSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => setSize((s) => (s.w === el.clientWidth && s.h === el.clientHeight ? s : { w: el.clientWidth, h: el.clientHeight }));
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

// Animation de la rotation (rAF uniquement pendant une transition ; chemin angulaire le plus court).
// Applique la rotation impérativement (aucun re-rendu React par frame).
export function useBearingAnimation({ innerRef, northRef, targetDeg, holdRef, onSettledNorth }) {
  const bearingRef = useRef(0);
  const goalRef = useRef(0);
  const rafRef = useRef(null);
  const settledCb = useRef(onSettledNorth);
  settledCb.current = onSettledNorth;

  const apply = useCallback((b) => {
    const el = innerRef.current;
    if (el) { el.style.transform = b ? `rotate(${-b}deg)` : ""; el.style.setProperty("--map-bearing", `${b}deg`); el.dataset.bearing = String(Math.round(normalizeDeg(b) * 10) / 10); }
    const n = northRef.current;
    if (n) n.style.transform = `rotate(${-b}deg)`;
  }, [innerRef, northRef]);

  const kick = useCallback(() => {
    if (rafRef.current != null || (holdRef && holdRef.current)) return;
    let last = null;
    const step = (ts) => {
      const dt = last == null ? 0.016 : Math.min(0.1, (ts - last) / 1000);
      last = ts;
      const diff = goalRef.current - bearingRef.current;
      if (Math.abs(diff) < 0.05) {
        bearingRef.current = goalRef.current;
        apply(bearingRef.current);
        rafRef.current = null;
        if (normalizeDeg(bearingRef.current) === 0) settledCb.current && settledCb.current();
        return;
      }
      bearingRef.current += diff * (1 - Math.exp(-dt * 7));
      apply(bearingRef.current);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, [apply, holdRef]);

  useEffect(() => {
    const cur = bearingRef.current;
    goalRef.current = cur + angleDelta(normalizeDeg(cur), normalizeDeg(targetDeg) ?? 0);
    kick();
  }, [targetDeg, kick]);

  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); rafRef.current = null; }, []);
  return { bearingRef, kick };
}

// Contrôleur monté DANS MapContainer : conversion pointeur, drag corrigé, détection d'un déplacement
// UTILISATEUR (dragstart — jamais déclenché par un recentrage programmatique), zoom centré en SUIVRE,
// invalidation de taille du cadre, recentrage SUIVRE sur changement de position (aucun timer).
export function MapRotationController({ outerRef, bearingRef, dragRef, kick, following, frameKey, followLatLng, onUserMove }) {
  const map = useMap();
  const followRef = useRef(following);
  followRef.current = following;
  const onUserMoveRef = useRef(onUserMove);
  onUserMoveRef.current = onUserMove;

  useEffect(() => {
    map.mouseEventToContainerPoint = (e) => {
      const el = outerRef.current;
      if (!el) return L.Map.prototype.mouseEventToContainerPoint.call(map, e);
      const [x, y] = screenToContainerPoint(e.clientX, e.clientY, el.getBoundingClientRect(), map.getSize(), bearingRef.current);
      return L.point(x, y);
    };
    const drag = map.dragging && map.dragging._draggable;
    const onPredrag = () => {
      const b = bearingRef.current;
      if (!b || !drag || !drag._newPos || !drag._startPos) return;
      const off = drag._newPos.subtract(drag._startPos);
      const [dx, dy] = dragOffsetToMap(off.x, off.y, b);
      drag._newPos = drag._startPos.add(L.point(dx, dy));
    };
    const onDragStart = () => { dragRef.current = true; if (followRef.current && onUserMoveRef.current) onUserMoveRef.current(); };
    const onDragEnd = () => { dragRef.current = false; kick(); };
    if (drag) drag.on("predrag", onPredrag);
    map.on("dragstart", onDragStart);
    map.on("dragend", onDragEnd);
    return () => {
      delete map.mouseEventToContainerPoint;
      if (drag) drag.off("predrag", onPredrag);
      map.off("dragstart", onDragStart);
      map.off("dragend", onDragEnd);
    };
  }, [map, outerRef, bearingRef, dragRef, kick]);

  useEffect(() => {
    const mode = following ? "center" : true;
    map.options.scrollWheelZoom = mode;
    map.options.doubleClickZoom = mode;
    map.options.touchZoom = mode;
    if (map.keyboard) { if (following) map.keyboard.disable(); else map.keyboard.enable(); }
  }, [map, following]);

  useEffect(() => {
    const c = map.getCenter(), z = map.getZoom();
    map.invalidateSize({ pan: false });
    map.setView(c, z, { animate: false });
  }, [map, frameKey]);

  const lat = followLatLng ? followLatLng[0] : null, lng = followLatLng ? followLatLng[1] : null;
  useEffect(() => {
    if (lat == null || lng == null || dragRef.current) return;
    map.panTo([lat, lng], { animate: true, duration: 0.5 });
  }, [map, lat, lng, dragRef]);

  return null;
}
